import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Trophy,
  Zap,
  Hash,
  Heart,
} from "lucide-react";
// import { CosmoIdeas } from "@/components/feed/CosmoIdeas";
import { FeedPromptOfDay } from "@/components/feed/FeedPromptOfDay";
import { getCuratedContentGrouped, SHOW_INFO, CATEGORY_PILL_COLORS } from "@/data/curatedContent";
import type { CuratedContentItem } from "@/types/content";

interface LeaderboardUser {
  user_id: string;
  full_name: string;
  prompts?: number;
  total_points?: number;
  engagement?: number;
}

function LeaderboardList({ 
  users, 
  valueKey, 
  suffix = "" 
}: { 
  users: LeaderboardUser[]; 
  valueKey: keyof LeaderboardUser; 
  suffix?: string;
}) {
  if (users.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No data yet</p>;
  }

  return (
    <div className="space-y-2">
      {users.slice(0, 5).map((user, index) => (
        <div 
          key={user.user_id} 
          className="flex items-center justify-between py-1.5"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-medium text-muted-foreground w-4">
              {index + 1}.
            </span>
            <span className="truncate text-sm">{user.full_name}</span>
          </div>
          <span className="text-sm font-medium flex-shrink-0">
            {user[valueKey]}{suffix}
          </span>
        </div>
      ))}
    </div>
  );
}

export function FeedRightRail() {
  const { user, membership } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [leaderboardTimeframe, setLeaderboardTimeframe] = useState<'weekly' | 'alltime'>('weekly');
  const [pointsLeaderboard, setPointsLeaderboard] = useState<LeaderboardUser[]>([]);
  const [engagementLeaderboard, setEngagementLeaderboard] = useState<LeaderboardUser[]>([]);
  const [activeLeaderboard, setActiveLeaderboard] = useState<LeaderboardUser[]>([]);

  useEffect(() => {
    if (user && membership?.org_id) {
      fetchLeaderboards();
    }
  }, [user, membership?.org_id]);

  useEffect(() => {
    if (user && membership?.org_id) {
      fetchLeaderboards();
    }
  }, [leaderboardTimeframe]);

  const fetchLeaderboards = async () => {
    if (!membership?.org_id) return;
    setLoading(true);
    // Map legacy values to date params
    const startDate = leaderboardTimeframe === 'weekly' 
      ? new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() 
      : null;

    try {
      const [pointsRes, engagementRes, activeRes] = await Promise.all([
        supabase.rpc('get_org_points_leaderboard', { _org_id: membership.org_id, _start_date: startDate, _end_date: null, _limit: 5 } as any),
        supabase.rpc('get_org_engagement_leaderboard', { _org_id: membership.org_id, _start_date: startDate, _end_date: null, _limit: 5 } as any),
        supabase.rpc('get_org_active_leaderboard', { _org_id: membership.org_id, _start_date: startDate, _end_date: null, _limit: 5 } as any),
      ]);
      if (pointsRes.data) setPointsLeaderboard(pointsRes.data as LeaderboardUser[]);
      if (engagementRes.data) setEngagementLeaderboard(engagementRes.data as LeaderboardUser[]);
      if (activeRes.data) setActiveLeaderboard(activeRes.data as LeaderboardUser[]);
    } catch (error) {
      console.error("Error fetching leaderboards:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="sticky top-4 space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-56 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="sticky top-4 space-y-4">
      {/* Prompt of the Day - consolidated component */}
      <FeedPromptOfDay variant="compact" />

      {/* CosmoIdeas - hidden, kept for future use */}
      {/* <CosmoIdeas
        layout="compact"
        onBuildPrompt={(initialGoal) => {
          navigate('/prompt', {
            state: { remixContext: { originalPrompt: initialGoal } }
          });
        }}
      /> */}

      {/* How others are using AI - curated content */}
      <HowOthersUsingAI />

      {/* Leaderboards - hidden for now, re-enable by uncommenting */}
      {/* <Card className="border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Trophy className="h-4 w-4 text-yellow-500" />
              Leaderboards
            </CardTitle>
            <Select 
              value={leaderboardTimeframe} 
              onValueChange={(value: 'weekly' | 'alltime') => setLeaderboardTimeframe(value)}
            >
              <SelectTrigger className="w-20 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="alltime">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="points" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-3 h-8">
              <TabsTrigger value="points" className="text-xs px-1">
                <Zap className="h-3 w-3 mr-0.5" />
                Points
              </TabsTrigger>
              <TabsTrigger value="engagement" className="text-xs px-1">
                <Heart className="h-3 w-3 mr-0.5" />
                Engage
              </TabsTrigger>
              <TabsTrigger value="prompts" className="text-xs px-1">
                <Hash className="h-3 w-3 mr-0.5" />
                Prompts
              </TabsTrigger>
            </TabsList>

            <TabsContent value="points" className="mt-0">
              <LeaderboardList users={pointsLeaderboard} valueKey="total_points" suffix=" pts" />
            </TabsContent>
            <TabsContent value="engagement" className="mt-0">
              <LeaderboardList users={engagementLeaderboard} valueKey="engagement" suffix="" />
            </TabsContent>
            <TabsContent value="prompts" className="mt-0">
              <LeaderboardList users={activeLeaderboard} valueKey="prompts" suffix="" />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card> */}

    </div>
  );
}

function HowOthersUsingAI() {
  const curatedGroups = getCuratedContentGrouped();
  const allItems = curatedGroups.flatMap((g) => g.items);
  
  // Pick 5 diverse items (different shows preferred)
  const picked: CuratedContentItem[] = [];
  const usedShows = new Set<string>();
  for (const item of allItems) {
    if (picked.length >= 5) break;
    if (!usedShows.has(item.show)) {
      picked.push(item);
      usedShows.add(item.show);
    }
  }
  for (const item of allItems) {
    if (picked.length >= 5) break;
    if (!picked.includes(item)) picked.push(item);
  }

  return (
    <div>
      <h3
        className="text-xs font-bold uppercase tracking-widest text-muted-foreground"
        style={{ marginBottom: 16, letterSpacing: "0.1em" }}
      >
        How others are using AI
      </h3>
      <div className="space-y-1">
        {picked.map((item) => {
          const show = SHOW_INFO[item.show];
          const pill = CATEGORY_PILL_COLORS[item.category];
          return (
            <Link
              key={item.id}
              to={`/ai-ideas/${item.id}`}
              className="block rounded-lg p-3 -mx-3 hover:bg-muted/40 transition-colors"
            >
              <div className="flex items-center gap-2 mb-1.5">
                {show && (
                  <img
                    src={show.logo}
                    alt={item.show}
                    className="h-5 w-5 rounded-full object-cover"
                  />
                )}
                <span className="text-xs font-medium text-muted-foreground">
                  {item.show}
                </span>
              </div>
              <p className="text-sm font-semibold text-foreground leading-snug line-clamp-2">
                {item.title}
              </p>
              {pill && (
                <Badge
                  variant="secondary"
                  className={`text-[10px] mt-2 ${pill.bg} ${pill.text} ${pill.border}`}
                >
                  {item.category}
                </Badge>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
